import { ENDPOINT } from '~/constants/endpoints';

import { type KyRequestOptions } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils/logging';

import { type SupplierSuggestionsResponse } from './types';
import { zodSupplierSuggestionsResponseSchema } from './validations';

type SupplierSuggestionsParams = {
  limit?: number;
  query?: string;
};

/**
 * Fetches supplier suggestions from the caller's past delivery notes.
 * Results include mapped companies and name-only free-text suppliers.
 *
 * @param params - Optional typeahead query and result limit.
 * @param options - Optional request options, such as an abort signal.
 * @returns Mixed supplier suggestions ranked by backend usage and recency.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/DLN-Suggestions/operation/get_supplier_suggestions_delivery_note_suggestions_suppliers_get
 */
export const fetchSupplierSuggestions = async (
  { limit, query }: SupplierSuggestionsParams,
  options?: KyRequestOptions,
): Promise<SupplierSuggestionsResponse> => {
  const searchParams = {
    ...(limit !== undefined && { limit }),
    ...(query && { query }),
  };

  const response = await vestigasApi
    .get(ENDPOINT.DELIVERY_NOTE.GET_SUPPLIER_SUGGESTIONS(), {
      searchParams,
      ...options,
    })
    .json<SupplierSuggestionsResponse>();

  const validated = zodSupplierSuggestionsResponseSchema.safeParse(response);

  if (validated.error?.issues?.length) {
    log.error({
      message: 'Supplier suggestions API response has validation errors',
      error: {
        errors: validated.error.issues,
        errorMessage: validated.error.message,
        response,
      },
    });
  }

  return response;
};
